import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { getSuiteStandardFlexibleRates } from '../../../../../../utilities/planUtils';
import theme from '../../../../../ui-kit/themes/theme';
import { handlePointsBalance } from '../../../../../utilities/utils';
import { SummaryContent } from '../../../Content';
import { AdditionalCharges } from './AdditionalCharges';
import { Details } from './Details';
import { DueAmount } from './DueAmount';
import { Header as SuiteHeader } from './Header';
import Offers from './Offers';
import { PackagesList } from './PackageList';
import ParkingFee from './ParkingFee';
import Points from './Points';
import { Subtotal } from './Subtotal';
import { TotalSavings } from './TotalSavings';
import { Totals } from './Totals';

const SuitePlanContent = ({
  expanded,
  onPaymentPage,
  clearSelectedSuite,
  isHighestRatesEnabled,
  isModifyFlow,
  hiddenOfferPrefixes,
  appliedViaPromoBanner,
  shouldSavePoints,
  selectedSuite,
  isChangedToFlex,
  effectiveIsLoggedInForFlex,
  ...restProps
}) => {
  const {
    promotionalSavings,
    loyaltyOptIn,
    suiteTotal,
    userOffers,
    activeOffers,
    removeActiveOffer,
    showLoyalty,
    pointsBalance,
    isCartVariation,
    resortLocation,
    setCartReturnedPageLinkClicked,
    clearPlanPackages,
    deletePackage,
    upsertPackage,
    promoCode,
    pointAmount,
    suitePriceIncludedTaxesAndFees
  } = restProps;

  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme.breakpoints.lg})`
  });
  const iconSize = isDesktop ? '16px' : '24px';
  const [showBonusExcedent, setShowBonusExcedent] = useState(false);
  const renderHeader = expanded || onPaymentPage || isDesktop;
  const hasSuitePriceIncludedTaxesAndFees = suitePriceIncludedTaxesAndFees !== '[]';
  const renderTaxes = (onPaymentPage && !isCartVariation) || (expanded && hasSuitePriceIncludedTaxesAndFees);
  const renderExpandedNonPaymentPage = expanded && !onPaymentPage;
  const renderPackages = expanded;
  const renderTotal = ((expanded || isDesktop) && onPaymentPage && isDesktop) || (!isDesktop && onPaymentPage);
  const shouldDisplayRoomSavings = !isCartVariation && !promoCode && isHighestRatesEnabled;

  const handleSavings = () => {
    const roomSavings = ~~parseFloat(promotionalSavings.toFixed(2));
    const pointsSavings = shouldSavePoints
      ? 0
      : handlePointsBalance(showLoyalty, loyaltyOptIn, pointAmount, suiteTotal);
    let offersSavings = 0;
    if (userOffers && userOffers.length > 0) {
      offersSavings = userOffers.reduce((previousValue, currentValue) => {
        if (activeOffers.length > 0 && activeOffers.includes(currentValue.id)) {
          return previousValue + currentValue.dollarValue;
        } else {
          return previousValue;
        }
      }, 0);
    }
    return Number(roomSavings) + Number(pointsSavings) + offersSavings;
  };

  const { flexibleRate } = getSuiteStandardFlexibleRates(selectedSuite, effectiveIsLoggedInForFlex);

  return (
    <SummaryContent fontSize={[14, null, null, 0]}>
      {renderHeader && !isCartVariation ? <SuiteHeader {...restProps} /> : null}
      {expanded ? (
        <Details
          {...restProps}
          setCartReturnedPageLinkClicked={setCartReturnedPageLinkClicked}
          clearPlanPackages={clearPlanPackages}
          clearSelectedSuite={clearSelectedSuite}
          handleSavings={handleSavings}
          iconSize={iconSize}
          isChangedToFlex={isChangedToFlex}
          flexibleRate={flexibleRate}
          selectedSuite={selectedSuite}
          shouldDisplayRoomSavings={shouldDisplayRoomSavings}
          isModifyFlow={isModifyFlow}
          hiddenOfferPrefixes={hiddenOfferPrefixes}
          appliedViaPromoBanner={appliedViaPromoBanner}
        />
      ) : null}
      {renderPackages ? (
        <PackagesList
          {...restProps}
          setCartReturnedPageLinkClicked={setCartReturnedPageLinkClicked}
          deletePackage={deletePackage}
          upsertPackage={upsertPackage}
        />
      ) : null}
      {showLoyalty && expanded && userOffers ? (
        <Offers
          isCartVariation={isCartVariation}
          userOffers={userOffers}
          activeOffers={activeOffers}
          removeOffer={removeActiveOffer}
          excedent={showBonusExcedent}
        />
      ) : null}
      {showLoyalty && expanded && pointsBalance ? (
        <Points {...restProps} isCartVariation={isCartVariation} shouldSavePoints={shouldSavePoints} />
      ) : null}

      {renderTaxes ? (
        <AdditionalCharges
          {...restProps}
          isModifyFlow={isModifyFlow}
          isDesktop={isDesktop}
          shouldSavePoints={shouldSavePoints}
        />
      ) : null}
      {expanded && !isCartVariation ? (
        <TotalSavings handleSavings={handleSavings} shouldDisplayTotalSavings={isHighestRatesEnabled} {...restProps} />
      ) : null}
      {renderExpandedNonPaymentPage && !isCartVariation ? (
        <Subtotal
          {...restProps}
          setExcedent={setShowBonusExcedent}
          resortLocation={resortLocation}
          hasSuitePriceIncludedTaxesAndFees={hasSuitePriceIncludedTaxesAndFees}
          shouldSavePoints={shouldSavePoints}
        />
      ) : null}

      {renderTotal && !isCartVariation ? (
        <Totals
          {...restProps}
          setExcedent={setShowBonusExcedent}
          resortLocation={resortLocation}
          shouldSavePoints={shouldSavePoints}
        />
      ) : null}
      {renderTotal && !isCartVariation ? (
        <DueAmount
          {...restProps}
          isChangedToFlex={isChangedToFlex}
          selectedSuite={selectedSuite}
          shouldSavePoints={shouldSavePoints}
        />
      ) : null}
      {renderTotal && !renderExpandedNonPaymentPage && !isCartVariation ? <ParkingFee /> : null}
      {expanded && isCartVariation && handleSavings() ? (
        <TotalSavings
          handleSavings={handleSavings}
          {...restProps}
          isCartVariation={isCartVariation}
          shouldDisplayTotalSavings={isHighestRatesEnabled}
        />
      ) : null}
    </SummaryContent>
  );
};

export default SuitePlanContent;
